import React, { useState, useEffect, useCallback, useRef } from "react";

import { getAuthStatus, getMe, listUsers, createUser, updateUser, deleteUser, uploadAvatar, listGroups, createGroup as apiCreateGroup, updateGroup as apiUpdateGroup, deleteGroup as apiDeleteGroup, addGroupMembers, removeGroupMembers, createInvite, listInvites, revokeInvite } from "../../../api";
import { validatePassword, PasswordRules } from "../../../utils/passwordRules";
import PasswordInput from "../../../components/PasswordInput";
import Select from "../../../components/Select";
import { useNotify } from "../../../ToastContext";
import xmarkIcon from "../../../assets/xmark.svg";
import plusIcon from "../../../assets/plus.svg";
import searchIconSrc from "../../../assets/search.svg";
import mailIcon from "../../../assets/mail.svg";

const ROLE_COLORS = {
  admin: { bg: "rgba(80,140,255,0.15)", color: "rgba(120,170,255,0.95)", border: "rgba(80,140,255,0.3)" },
  engineer: { bg: "rgba(76,175,80,0.12)", color: "rgba(120,200,120,0.95)", border: "rgba(76,175,80,0.25)" },
  viewer: { bg: "var(--gw-surface-2)", color: "var(--gw-muted)", border: "var(--gw-border)" },
};

const RoleBadge = ({ role }) => {
  const c = ROLE_COLORS[role] || ROLE_COLORS.viewer;
  return (
    <span style={{
      fontSize: 11, textTransform: "capitalize", padding: "3px 10px",
      borderRadius: 6, background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      fontWeight: 500, letterSpacing: 0.3,
    }}>
      {role}
    </span>
  );
};

const UserAvatar = ({ user, size = 32 }) => {
  const hasAvatar = user.avatar;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0, overflow: "hidden",
      background: hasAvatar ? "transparent" : "rgba(80,140,255,0.15)",
      border: hasAvatar ? "none" : "1px solid rgba(80,140,255,0.25)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.4, fontWeight: 600, color: "rgba(140,180,255,0.95)",
    }}>
      {hasAvatar
        ? <img src={user.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
        : (user.username || "?")[0].toUpperCase()
      }
    </div>
  );
};

const AvatarUpload = ({ value, onChange, fallbackInitial, userId }) => {
  const ref = useRef(null);
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = async () => {
      URL.revokeObjectURL(url);
      const max = 256;
      let w = img.width, h = img.height;
      if (w > max || h > max) { const s = max / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      const dataUrl = c.toDataURL("image/jpeg", 0.85);
      try {
        const res = await uploadAvatar(dataUrl, userId, value);
        if (res?.url) onChange(res.url);
      } catch {
        onChange(dataUrl); // fallback to base64 if upload fails
      }
    };
    img.src = url;
  };
  return (
    <div className="gwAvatarUpload" onClick={() => ref.current?.click()} title="Click to upload photo">
      {value ? (
        <img src={value} alt="Avatar" className="gwAvatarImage" />
      ) : (
        <span className="gwAvatarInitial">{fallbackInitial}</span>
      )}
      <div className="gwAvatarOverlay">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
      </div>
      {value && (
        <button type="button" className="gwAvatarRemove" title="Remove photo"
          onClick={(e) => { e.stopPropagation(); onChange(""); if (ref.current) ref.current.value = ""; }}>
          <img src={xmarkIcon} alt="" style={{ width: 8, height: 8, filter: "brightness(0) invert(1)" }} />
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
};

const UserManagementPage = () => {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authMode, setAuthMode] = useState("internal");
  const [subscriptionTrialing, setSubscriptionTrialing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const notify = useNotify();

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "", password: "", confirmPassword: "", role: "viewer",
    first_name: "", last_name: "", email: "", phone: "", department: "", location: "",
  });
  const setNewField = (k, v) => setNewUser((p) => ({ ...p, [k]: v }));

  // Groups
  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [editingGroup, setEditingGroup] = useState(null); // group id or "new"
  const [groupSearchQuery, setGroupSearchQuery] = useState("");
  const [groupTab, setGroupTab] = useState("members");
  const [deleteGroupTarget, setDeleteGroupTarget] = useState(null);
  const [, setDeletingGroup] = useState(false);

  // Invites
  const [invites, setInvites] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "viewer" });
  const [inviteResult, setInviteResult] = useState(null); // { invite_url, email_sent }
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const setInviteField = (k, v) => setInviteForm((p) => ({ ...p, [k]: v }));

  // AI propose-user / propose-group: open the matching create form pre-filled.
  useEffect(() => {
    const userHandler = (e) => {
      const payload = e.detail?.payload;
      if (!payload || typeof payload !== "object") return;
      setNewUser({
        username: payload.username || "",
        password: "",
        confirmPassword: "",
        role: payload.role || "viewer",
        first_name: payload.first_name || "",
        last_name: payload.last_name || "",
        email: payload.email || "",
        phone: "",
        department: "",
        location: "",
      });
      setShowCreate(true);
    };
    const groupHandler = (e) => {
      const payload = e.detail?.payload;
      if (!payload || typeof payload !== "object") return;
      if (typeof payload.name === "string") setNewGroupName(payload.name);
      setEditingGroup("new");
    };
    window.addEventListener("gw-ai-propose-user-page", userHandler);
    window.addEventListener("gw-ai-propose-group-page", groupHandler);
    return () => {
      window.removeEventListener("gw-ai-propose-user-page", userHandler);
      window.removeEventListener("gw-ai-propose-group-page", groupHandler);
    };
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const data = await listGroups();
      // API returns array directly, normalize member_ids → userIds
      const raw = Array.isArray(data) ? data : (data.groups || data || []);
      const normalized = raw.map((g) => ({
        ...g, userIds: g.member_ids || [],
      }));
      setGroups(normalized);
    } catch {}
  }, []);

  const loadInvites = useCallback(async () => {
    try {
      const data = await listInvites();
      setInvites(data.invites || []);
    } catch {}
  }, []);

  const handleCreateInvite = async () => {
    if (!inviteForm.email) { notify({ type: "error", title: "Error", message: "Email is required." }); return; }
    setInviting(true);
    try {
      const res = await createInvite({ email: inviteForm.email, role: inviteForm.role });
      setInviteResult(res);
      await loadInvites();
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to create invite" });
    } finally {
      setInviting(false);
    }
  };

  const handleRevokeInvite = async (id) => {
    setRevoking(true);
    try {
      await revokeInvite(id);
      notify({ type: "success", title: "Revoked", message: "Invite has been revoked." });
      setRevokeTarget(null);
      await loadInvites();
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to revoke invite" });
    } finally {
      setRevoking(false);
    }
  };

  const copyInviteLink = (url) => {
    const full = window.location.origin + url;
    navigator.clipboard.writeText(full).then(
      () => notify({ type: "success", title: "Copied", message: "Invite link copied to clipboard." }),
      () => notify({ type: "error", title: "Error", message: "Failed to copy link." })
    );
  };

  const handleResendInvite = async (inv) => {
    try {
      await revokeInvite(inv.id);
      const res = await createInvite({ email: inv.email, role: inv.role });
      setInviteForm({ email: inv.email, role: inv.role });
      setInviteResult(res);
      setShowInvite(true);
      await loadInvites();
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to resend invite" });
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const data = await apiCreateGroup({ name: newGroupName.trim() });
      // API returns group directly
      const newId = data?.id || data?.group?.id;
      setNewGroupName("");
      await loadGroups();
      if (newId) {
        setEditingGroup(newId);
        setGroupTab("add");
      }
      notify({ type: "success", title: "Created", message: `Group created. Add members below.` });
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to create group" });
    }
  };

  const confirmDeleteGroup = async () => {
    if (!deleteGroupTarget) return;
    const group = groups.find((g) => g.id === deleteGroupTarget);
    setDeletingGroup(true);
    try {
      await apiDeleteGroup(deleteGroupTarget);
      setDeleteGroupTarget(null);
      setEditingGroup(null);
      await loadGroups();
      notify({ type: "success", title: "Deleted", message: `"${group?.name}" removed.` });
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to delete group" });
    } finally {
      setDeletingGroup(false);
    }
  };

  const toggleUserInGroup = async (groupId, userId) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    const isMember = group.userIds.includes(userId);
    // Optimistic update first for snappy UI
    setGroups((prev) => prev.map((g) => {
      if (g.id !== groupId) return g;
      return { ...g, userIds: isMember ? g.userIds.filter((id) => id !== userId) : [...g.userIds, userId] };
    }));
    try {
      if (isMember) {
        await removeGroupMembers(groupId, [userId]);
      } else {
        await addGroupMembers(groupId, [userId]);
      }
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to update members" });
      await loadGroups(); // revert on error
    }
  };

  const renameGroup = async (groupId, name) => {
    // Optimistic update
    setGroups((prev) => prev.map((g) => g.id === groupId ? { ...g, name } : g));
    try {
      await apiUpdateGroup(groupId, { name });
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to rename group" });
      await loadGroups();
    }
  };

  // Edit modal
  const [editTarget, setEditTarget] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const setEditField = (k, v) => setEditFields((p) => ({ ...p, [k]: v }));

  const loadData = useCallback(async () => {
    try {
      const [me, data] = await Promise.all([getMe(), listUsers()]);
      setIsAdmin((me.permissions || []).includes("admin"));
      setCurrentUserId(me.user_id);
      setUsers(data.users || []);
    } catch (err) { notify({ type: "error", title: "Error", message: err.message || "Failed to load users" }); }
    finally { setLoading(false); }
    // Auth mode is best-effort — a failure just keeps the default ("internal"),
    // which shows the Add User button as a safe fallback.
    try {
      const status = await getAuthStatus();
      setAuthMode(status.auth_mode || "internal");
      setSubscriptionTrialing(!!status.subscription_trialing);
    } catch {}
  }, [notify]);

  useEffect(() => { loadData(); loadGroups(); loadInvites(); }, [loadData, loadGroups, loadInvites]);

  // Refresh when the AI mutates a user/role/invite/group.
  useEffect(() => {
    const handler = (e) => {
      const r = e.detail?.resource;
      if (r === "users") loadData();
      else if (r === "invites") loadInvites();
      else if (r === "groups") loadGroups();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [loadData, loadGroups, loadInvites]);

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password || !newUser.confirmPassword) { notify({ type: "error", title: "Error", message: "Username and password are required." }); return; }
    if (newUser.password !== newUser.confirmPassword) { notify({ type: "error", title: "Error", message: "Passwords do not match." }); return; }
    setCreating(true);
    try {
      await createUser({ username: newUser.username, password: newUser.password, role: newUser.role });
      // If there's optional metadata, update it after creation
      const meta = {};
      for (const k of ["first_name", "last_name", "email", "phone", "department", "location"]) {
        if (newUser[k]) meta[k] = newUser[k];
      }
      if (Object.keys(meta).length > 0) {
        // Need user id — reload to find it
        const data = await listUsers();
        const created = (data.users || []).find((u) => u.username === newUser.username);
        if (created) await updateUser(created.user_id, meta);
      }
      notify({ type: "success", title: "User Created", message: `"${newUser.username}" has been added.` });
      setNewUser({ username: "", password: "", confirmPassword: "", role: "viewer",
        first_name: "", last_name: "", email: "", phone: "", department: "", location: "" });
      setShowCreate(false);
      await loadData();
    } catch (err) { notify({ type: "error", title: "Error", message: err.message || "Failed to create user" }); }
    finally { setCreating(false); }
  };

  const openEditModal = (user) => {
    setEditTarget(user);
    setEditFields({
      role: user.role, email: user.email || "", first_name: user.first_name || "",
      last_name: user.last_name || "", phone: user.phone || "", department: user.department || "",
      location: user.location || "", avatar: user.avatar || "",
    });
    setDeleteTarget(null);
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      let fields = { ...editFields };
      // If avatar is base64, upload it first
      if (fields.avatar && fields.avatar.startsWith("data:")) {
        try {
          const res = await uploadAvatar(fields.avatar, editTarget.user_id, editTarget.avatar);
          if (res?.url) fields.avatar = res.url;
        } catch {}
      }
      await updateUser(editTarget.user_id, fields);
      notify({ type: "success", title: "Updated", message: `"${editTarget.username}" has been updated.` });
      setEditTarget(null);
      await loadData();
    } catch (err) { notify({ type: "error", title: "Error", message: err.message || "Failed to update user" }); }
    finally { setEditSaving(false); }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteUser(deleteTarget.user_id);
      notify({ type: "success", title: "Deleted", message: `"${deleteTarget.username}" has been removed.` });
      setDeleteTarget(null); setEditTarget(null);
      await loadData();
    } catch (err) { notify({ type: "error", title: "Error", message: err.message || "Failed to delete user" }); setDeleteTarget(null); }
    finally { setDeleting(false); }
  };

  const filteredUsers = users.filter((u) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return u.username?.toLowerCase().includes(q) || u.role?.toLowerCase().includes(q)
      || u.email?.toLowerCase().includes(q) || u.first_name?.toLowerCase().includes(q)
      || u.last_name?.toLowerCase().includes(q);
  });

  const editDirty = editTarget && JSON.stringify(editFields) !== JSON.stringify({
    role: editTarget.role, email: editTarget.email || "", first_name: editTarget.first_name || "",
    last_name: editTarget.last_name || "", phone: editTarget.phone || "", department: editTarget.department || "",
    location: editTarget.location || "", avatar: editTarget.avatar || "",
  });

  if (loading) {
    return (
      <div className="gwContentGrid"><section className="gwFormStack">
        <div className="gwPageHeader"><div>
          <div className="gwPageTitle">User Management</div>
          <div className="gwPageSubtitle">Loading...</div>
        </div></div>
      </section></div>
    );
  }

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">User Management</div>
            <div className="gwPageSubtitle">{users.length} user{users.length !== 1 ? "s" : ""} registered</div>
          </div>
          {isAdmin && (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="gwGhostBtn" type="button"
                onClick={() => { setShowInvite(true); setInviteForm({ email: "", role: "viewer" }); setInviteResult(null); }}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <img src={mailIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                <span>Invite User</span>
              </button>
              {authMode !== "oidc" && (
                <button className="gwPrimaryBtn" type="button"
                  onClick={() => { setShowCreate(true); setNewUser({ username: "", password: "", confirmPassword: "", role: "viewer", first_name: "", last_name: "", email: "", phone: "", department: "", location: "" }); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                  <span>Add User</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Create user modal */}
        {showCreate && isAdmin && (
          <div className="gwOverlay" onClick={() => setShowCreate(false)}>
            <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 540 }}>
              <div className="gwModalHeader">
                <div>
                  <div className="gwModalTitle">New User</div>
                  <div className="gwModalSubtitle">Create a new user account</div>
                </div>
                <button className="gwModalClose" type="button" onClick={() => setShowCreate(false)}>
                  <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
                </button>
              </div>
              <div className="gwModalBody">
                <div className="gwFieldRow">
                  <div className="gwField">
                    <label className="gwLabel">Username *</label>
                    <input className="gwInput" value={newUser.username} onChange={(e) => setNewField("username", e.target.value)} autoComplete="off" autoFocus />
                  </div>
                  <div className="gwField">
                    <label className="gwLabel">Role</label>
                    <Select
                      value={newUser.role}
                      onChange={(v) => setNewField("role", v)}
                      options={[
                        { value: "viewer", label: "Viewer" },
                        { value: "engineer", label: "Engineer" },
                        { value: "admin", label: "Admin" },
                      ]}
                    />
                  </div>
                </div>
                <div className="gwFieldRow">
                  <div className="gwField">
                    <label className="gwLabel">Password *</label>
                    <PasswordInput value={newUser.password} onChange={(e) => setNewField("password", e.target.value)} autoComplete="new-password" />
                    <PasswordRules password={newUser.password} />
                  </div>
                  <div className="gwField">
                    <label className="gwLabel">Confirm Password *</label>
                    <PasswordInput value={newUser.confirmPassword} onChange={(e) => setNewField("confirmPassword", e.target.value)} autoComplete="new-password" />
                  </div>
                </div>

                <div style={{ borderTop: "1px solid var(--gw-border)", paddingTop: 12, marginTop: 4 }}>
                  <div style={{ fontSize: 11, color: "var(--gw-muted)", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600 }}>Optional Profile Info</div>
                  <div className="gwFieldRow">
                    <div className="gwField">
                      <label className="gwLabel">First Name</label>
                      <input className="gwInput" value={newUser.first_name} onChange={(e) => setNewField("first_name", e.target.value)} />
                    </div>
                    <div className="gwField">
                      <label className="gwLabel">Last Name</label>
                      <input className="gwInput" value={newUser.last_name} onChange={(e) => setNewField("last_name", e.target.value)} />
                    </div>
                  </div>
                  <div className="gwFieldRow" style={{ marginTop: 10 }}>
                    <div className="gwField">
                      <label className="gwLabel">Email</label>
                      <input className="gwInput" type="email" value={newUser.email} onChange={(e) => setNewField("email", e.target.value)} />
                    </div>
                    <div className="gwField">
                      <label className="gwLabel">Phone</label>
                      <input className="gwInput" type="tel" value={newUser.phone} onChange={(e) => setNewField("phone", e.target.value)} />
                    </div>
                  </div>
                  <div className="gwFieldRow" style={{ marginTop: 10 }}>
                    <div className="gwField">
                      <label className="gwLabel">Department</label>
                      <input className="gwInput" value={newUser.department} onChange={(e) => setNewField("department", e.target.value)} />
                    </div>
                    <div className="gwField">
                      <label className="gwLabel">Location</label>
                      <input className="gwInput" value={newUser.location} onChange={(e) => setNewField("location", e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="gwPrimaryBtn" type="button" onClick={handleCreateUser}
                    disabled={creating || !newUser.username || !newUser.password || !newUser.confirmPassword || !validatePassword(newUser.password)}>
                    {creating ? "Creating..." : "Create User"}
                  </button>
                  <button className="gwGhostBtn" type="button" onClick={() => setShowCreate(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User list */}
        <div className="gwCardSection">
          <div className="gwSectionTitle" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Users</span>
            <input className="gwInput" type="text" placeholder="Search..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} style={{ width: 200, height: 30, fontSize: 12 }} />
          </div>
          <div className="gwCardSectionBody" style={{ padding: 0 }}>
            {filteredUsers.map((u) => {
              const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ");
              return (
                <div key={u.user_id} className="umUserRow"
                  data-gw-resource="users" data-gw-id={u.user_id} data-gw-name={u.username}
                  onClick={() => isAdmin && openEditModal(u)}
                  style={{ cursor: isAdmin ? "pointer" : "default" }}>
                  <UserAvatar user={u} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="umUserName">{u.username}</span>
                      {fullName && <span style={{ fontSize: 12, color: "var(--gw-text-secondary)" }}>{fullName}</span>}
                      {u.user_id === currentUserId && <span className="umYouBadge">you</span>}
                    </div>
                    <div className="umUserMeta">
                      {u.email || "No email"}
                      {u.department ? ` \u00b7 ${u.department}` : ""}
                      {u.location ? ` \u00b7 ${u.location}` : ""}
                    </div>
                  </div>
                  <RoleBadge role={u.role} />
                  <div className="umUserDate">
                    {u.last_login_at ? `Active ${new Date(u.last_login_at).toLocaleDateString()}`
                      : u.created_at ? `Joined ${new Date(u.created_at).toLocaleDateString()}` : ""}
                  </div>
                  {isAdmin && (
                    <button className="gwGhostBtn umEditBtn" type="button"
                      onClick={(e) => { e.stopPropagation(); openEditModal(u); }}>Edit</button>
                  )}
                </div>
              );
            })}
            {filteredUsers.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>No users found.</div>
            )}
          </div>
        </div>

        {/* Pending Invites */}
        {isAdmin && invites.length > 0 && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Pending Invites</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {invites.map((inv) => {
                const expiresAt = new Date(inv.expires_at);
                const now = new Date();
                const hoursLeft = Math.max(0, Math.round((expiresAt - now) / (1000 * 60 * 60)));
                const expired = hoursLeft <= 0;
                const invitedByUser = users.find((u) => u.user_id === inv.invited_by);
                return (
                  <div key={inv.id} className="umUserRow"
                    data-gw-resource="invites" data-gw-id={inv.id} data-gw-name={inv.email}
                    style={{ cursor: "default" }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                      background: "rgba(255,180,50,0.12)", border: "1px solid rgba(255,180,50,0.25)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 15, color: "rgba(255,180,50,0.9)",
                    }}>
                      <img src={mailIcon} alt="" style={{ width: 16, height: 16, filter: "brightness(0) invert(1) sepia(1) saturate(3) hue-rotate(15deg)" }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="umUserName">{inv.email}</span>
                      </div>
                      <div className="umUserMeta">
                        {expired ? "Expired" : `Expires in ${hoursLeft}h`}
                        {invitedByUser ? ` \u00b7 Invited by ${invitedByUser.username}` : ""}
                      </div>
                    </div>
                    <RoleBadge role={inv.role} />
                    <div style={{ display: "flex", gap: 6 }}>
                      {revokeTarget === inv.id ? (
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <button className="gwDangerBtn" type="button" style={{ height: 30, fontSize: 12, padding: "0 10px" }}
                            onClick={() => handleRevokeInvite(inv.id)} disabled={revoking}>
                            {revoking ? "..." : "Confirm"}
                          </button>
                          <button className="gwGhostBtn" type="button" style={{ height: 30, fontSize: 12, padding: "0 8px" }}
                            onClick={() => setRevokeTarget(null)}>Cancel</button>
                        </div>
                      ) : (
                        <>
                          {!expired && (
                            <button className="gwGhostBtn" type="button" style={{ height: 30, fontSize: 12, padding: "0 10px" }}
                              onClick={() => handleResendInvite(inv)}>Resend</button>
                          )}
                          <button className="gwGhostBtn" type="button" style={{ height: 30, fontSize: 12, padding: "0 10px", color: "var(--gw-danger, #e57373)" }}
                            onClick={() => setRevokeTarget(inv.id)}>Revoke</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Invite user modal */}
        {showInvite && isAdmin && (
          <div className="gwOverlay" onClick={() => setShowInvite(false)}>
            <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 460 }}>
              <div className="gwModalHeader">
                <div>
                  <div className="gwModalTitle">{inviteResult ? "Invite Created" : "Invite User"}</div>
                  <div className="gwModalSubtitle">{inviteResult ? "Share the link below with the invitee." : "Send an invite link via email"}</div>
                </div>
                <button className="gwModalClose" type="button" onClick={() => setShowInvite(false)}>
                  <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
                </button>
              </div>
              <div className="gwModalBody">
                {!inviteResult && authMode === "oidc" && (
                  <div style={{
                    background: "var(--gw-warning)", border: "1px solid var(--gw-border-emphasis)",
                    borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 13,
                    color: "var(--gw-text)", fontWeight: 500,
                  }}>
                    {subscriptionTrialing
                      ? "Free during your trial. $10/mo per user after."
                      : "Adding a user will add $10/mo to your subscription."}
                  </div>
                )}
                {inviteResult ? (
                  <>
                    <div style={{ fontSize: 13, color: "var(--gw-text-secondary)", marginBottom: 12 }}>
                      Copy the link below and share it with <strong>{inviteForm.email}</strong>.
                    </div>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8,
                      background: "var(--gw-surface-1)", border: "1px solid var(--gw-border)",
                      borderRadius: 8, padding: "10px 14px",
                    }}>
                      <code style={{ flex: 1, fontSize: 12, wordBreak: "break-all", color: "var(--gw-text)" }}>
                        {window.location.origin}{inviteResult.invite_url}
                      </code>
                      <button className="gwPrimaryBtn" type="button" style={{ flexShrink: 0, height: 30, fontSize: 12, padding: "0 12px" }}
                        onClick={() => copyInviteLink(inviteResult.invite_url)}>
                        Copy
                      </button>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--gw-muted)", marginTop: 10 }}>
                      This link expires in 3 days.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="gwField">
                      <label className="gwLabel">Email *</label>
                      <input className="gwInput" type="email" placeholder="user@example.com"
                        value={inviteForm.email} onChange={(e) => setInviteField("email", e.target.value)}
                        autoFocus onKeyDown={(e) => e.key === "Enter" && inviteForm.email && handleCreateInvite()} />
                    </div>
                    <div className="gwField" style={{ marginTop: 12 }}>
                      <label className="gwLabel">Role</label>
                      <Select
                        value={inviteForm.role}
                        onChange={(v) => setInviteField("role", v)}
                        options={[
                          { value: "viewer", label: "Viewer" },
                          { value: "engineer", label: "Engineer" },
                          { value: "admin", label: "Admin" },
                        ]}
                      />
                      <span className="gwHint">The role assigned when the invitee creates their account.</span>
                    </div>
                  </>
                )}
              </div>
              <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
                {inviteResult ? (
                  <button className="gwGhostBtn" type="button" onClick={() => setShowInvite(false)}>Done</button>
                ) : (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="gwPrimaryBtn" type="button" onClick={handleCreateInvite}
                      disabled={inviting || !inviteForm.email}>
                      {inviting ? "Sending..." : "Send Invite"}
                    </button>
                    <button className="gwGhostBtn" type="button" onClick={() => setShowInvite(false)}>Cancel</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Groups */}
        {isAdmin && (
          <div className="gwCardSection">
            <div className="gwSectionTitle" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Groups</span>
              <button className="gwGhostBtn" type="button"
                onClick={() => { setEditingGroup("new"); setNewGroupName(""); setGroupSearchQuery(""); }}
                style={{ height: 28, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <img src={plusIcon} alt="" className="gwIcon" style={{ width: 12, height: 12 }} /> New Group
              </button>
            </div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {groups.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>
                  No groups yet. Groups let you organize users into teams, shifts, or departments.
                </div>
              )}
              {groups.map((group) => {
                const members = users.filter((u) => group.userIds.includes(u.user_id));
                const mc = members.length;
                return (
                  <div key={group.id} className="umUserRow"
                    data-gw-resource="groups" data-gw-id={group.id} data-gw-name={group.name}
                    style={{ cursor: "pointer" }}
                    onClick={() => { setEditingGroup(group.id); setGroupSearchQuery(""); }}>
                    <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, minWidth: 0 }}>
                      {/* Member avatar stack */}
                      <div style={{ display: "flex", flexShrink: 0 }}>
                        {members.slice(0, 3).map((u, i) => (
                          <div key={u.user_id} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: 3 - i }}>
                            <UserAvatar user={u} size={28} />
                          </div>
                        ))}
                        {mc === 0 && (
                          <div style={{
                            width: 28, height: 28, borderRadius: "50%", border: "1px dashed var(--gw-border-strong)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 12, color: "var(--gw-muted)",
                          }}>?</div>
                        )}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <span className="umUserName">{group.name}</span>
                        <div className="umUserMeta">{mc} member{mc !== 1 ? "s" : ""}</div>
                      </div>
                    </div>
                    <button className="gwGhostBtn umEditBtn" type="button"
                      onClick={(e) => { e.stopPropagation(); setEditingGroup(group.id); setGroupSearchQuery(""); }}>
                      Edit
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!isAdmin && <span className="gwHint">Only administrators can create, edit, or delete users.</span>}

      </section>

      {/* Edit modal */}
      {editTarget && (
        <div className="gwOverlay" onClick={() => setEditTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 500 }}>
            <div className="gwModalHeader">
              <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1 }}>
                <AvatarUpload
                  value={editFields.avatar}
                  onChange={(v) => setEditField("avatar", v)}
                  fallbackInitial={(editTarget.username || "?")[0].toUpperCase()}
                  userId={editTarget.user_id}
                />
                <div>
                  <div className="gwModalTitle">{editTarget.username}</div>
                  <div className="gwModalSubtitle">
                    {editTarget.created_at && `Joined ${new Date(editTarget.created_at).toLocaleDateString()}`}
                    {editTarget.last_login_at && ` \u00b7 Last active ${new Date(editTarget.last_login_at).toLocaleDateString()}`}
                  </div>
                </div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setEditTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>

            <div className="gwModalBody">
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">First Name</label>
                  <input className="gwInput" value={editFields.first_name} onChange={(e) => setEditField("first_name", e.target.value)} />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Last Name</label>
                  <input className="gwInput" value={editFields.last_name} onChange={(e) => setEditField("last_name", e.target.value)} />
                </div>
              </div>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">Email</label>
                  <input className="gwInput" type="email" value={editFields.email} onChange={(e) => setEditField("email", e.target.value)} />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Phone</label>
                  <input className="gwInput" type="tel" value={editFields.phone} onChange={(e) => setEditField("phone", e.target.value)} />
                </div>
              </div>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">Department</label>
                  <input className="gwInput" value={editFields.department} onChange={(e) => setEditField("department", e.target.value)} />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Location</label>
                  <input className="gwInput" value={editFields.location} onChange={(e) => setEditField("location", e.target.value)} />
                </div>
              </div>
              <div className="gwField">
                <label className="gwLabel">Role</label>
                {editTarget.user_id === currentUserId ? (
                  <>
                    <div className="gwInput" style={{ display: "flex", alignItems: "center", textTransform: "capitalize" }}>
                      {editFields.role}
                    </div>
                    <span className="gwHint">You cannot change your own role. Ask another admin.</span>
                  </>
                ) : (
                  <>
                    <Select
                      value={editFields.role}
                      onChange={(v) => setEditField("role", v)}
                      options={[
                        { value: "viewer", label: "Viewer" },
                        { value: "engineer", label: "Engineer" },
                        { value: "admin", label: "Admin" },
                      ]}
                    />
                    <span className="gwHint">Controls what the user can access and modify.</span>
                  </>
                )}
              </div>
            </div>

            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="gwPrimaryBtn" type="button" onClick={handleSaveEdit}
                  disabled={!editDirty || editSaving}>{editSaving ? "Saving..." : "Save"}</button>
                <button className="gwGhostBtn" type="button" onClick={() => setEditTarget(null)}>Cancel</button>
              </div>
              {editTarget.user_id !== currentUserId && (
                !deleteTarget ? (
                  <button className="gwDangerBtn" type="button" onClick={() => setDeleteTarget(editTarget)}
                    style={{ fontSize: 12 }}>Delete</button>
                ) : (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <button className="gwDangerBtn" type="button" onClick={handleDeleteUser}
                      disabled={deleting}>{deleting ? "..." : "Confirm"}</button>
                    <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}
                      style={{ fontSize: 12 }}>Cancel</button>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Group modal (create + edit) */}
      {editingGroup && (() => {
        const isNew = editingGroup === "new";
        const group = isNew ? null : groups.find((g) => g.id === editingGroup);
        if (!isNew && !group) return null;

        const memberIds = group ? group.userIds : [];
        const members = users.filter((u) => memberIds.includes(u.user_id));
        const nonMembers = users.filter((u) => !memberIds.includes(u.user_id));

        const q = groupSearchQuery.toLowerCase();
        const filterUser = (u) => !q || u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
          || u.first_name?.toLowerCase().includes(q) || u.last_name?.toLowerCase().includes(q);

        const filteredMembers = members.filter(filterUser);
        const filteredNonMembers = nonMembers.filter(filterUser);

        const UserRow = ({ u, action, actionLabel, actionStyle }) => {
          const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ");
          return (
            <div style={{
              display: "flex", alignItems: "center", gap: 12, padding: "10px 20px",
              borderBottom: "1px solid var(--gw-border)",
              transition: "background 0.1s",
            }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--gw-surface-1)"}
              onMouseLeave={(e) => e.currentTarget.style.background = ""}
            >
              <UserAvatar user={u} size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {u.username}
                  {fullName && <span style={{ fontWeight: 400, color: "var(--gw-text-secondary)", marginLeft: 6 }}>{fullName}</span>}
                </div>
                <div style={{ fontSize: 12, color: "var(--gw-muted)" }}>
                  {u.email || "No email"}{u.role ? ` \u00b7 ${u.role}` : ""}
                </div>
              </div>
              <button className={actionStyle || "gwGhostBtn"} type="button"
                onClick={action} style={{ height: 30, fontSize: 12, padding: "0 12px", flexShrink: 0 }}>
                {actionLabel}
              </button>
            </div>
          );
        };

        return (
          <div className="gwOverlay" onClick={() => { setEditingGroup(null); setGroupTab("members"); setGroupSearchQuery(""); }}>
            <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 580, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
              <div className="gwModalHeader">
                <div style={{ flex: 1 }}>
                  {isNew ? (
                    <div className="gwModalTitle">New Group</div>
                  ) : (
                    <input className="gwInput" value={group.name}
                      onChange={(e) => renameGroup(group.id, e.target.value)}
                      style={{ fontWeight: 600, fontSize: 16, border: "none", background: "transparent", padding: 0, height: "auto" }} />
                  )}
                  <div className="gwModalSubtitle">
                    {isNew ? "Name the group, then add members." : `${members.length} member${members.length !== 1 ? "s" : ""} \u00b7 ${nonMembers.length} available`}
                  </div>
                </div>
                <button className="gwModalClose" type="button" onClick={() => { setEditingGroup(null); setGroupTab("members"); setGroupSearchQuery(""); }}>
                  <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
                </button>
              </div>

              {isNew ? (
                <div className="gwModalBody">
                  <div className="gwField">
                    <label className="gwLabel">Group Name</label>
                    <input className="gwInput" placeholder="e.g. Operators, Shift B, Engineering"
                      value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleCreateGroup()} autoFocus />
                  </div>
                </div>
              ) : (
                <>
                  {/* Tabs */}
                  <div style={{ display: "flex", borderBottom: "1px solid var(--gw-border)", padding: "0 20px", flexShrink: 0 }}>
                    <button type="button" onClick={() => { setGroupTab("members"); setGroupSearchQuery(""); }}
                      style={{
                        padding: "10px 16px", fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer",
                        background: "transparent",
                        color: groupTab === "members" ? "var(--gw-text)" : "var(--gw-muted)",
                        borderBottom: groupTab === "members" ? "2px solid var(--gw-accent)" : "2px solid transparent",
                        transition: "all 0.15s",
                      }}>
                      Members ({members.length})
                    </button>
                    <button type="button" onClick={() => { setGroupTab("add"); setGroupSearchQuery(""); }}
                      style={{
                        padding: "10px 16px", fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer",
                        background: "transparent",
                        color: groupTab === "add" ? "var(--gw-text)" : "var(--gw-muted)",
                        borderBottom: groupTab === "add" ? "2px solid var(--gw-accent)" : "2px solid transparent",
                        transition: "all 0.15s",
                      }}>
                      Add Users ({nonMembers.length})
                    </button>
                  </div>

                  {/* Search */}
                  <div style={{ padding: "12px 20px 0", flexShrink: 0 }}>
                    <div className="gwSearch" style={{ width: "100%", height: 36 }}>
                      <img src={searchIconSrc} alt="" className="gwSearchIcon" />
                      <input
                        placeholder={groupTab === "members" ? "Search members..." : "Search users to add..."}
                        value={groupSearchQuery} onChange={(e) => setGroupSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* User list */}
                  <div style={{ flex: 1, overflow: "auto", padding: "8px 0" }}>
                    {groupTab === "members" && (
                      <>
                        {filteredMembers.map((u) => (
                          <UserRow key={u.user_id} u={u} actionLabel="Remove"
                            action={() => toggleUserInGroup(group.id, u.user_id)} />
                        ))}
                        {filteredMembers.length === 0 && (
                          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>
                            {groupSearchQuery ? "No members match." : "No members yet. Switch to 'Add Users' to add some."}
                          </div>
                        )}
                      </>
                    )}
                    {groupTab === "add" && (
                      <>
                        {filteredNonMembers.map((u) => (
                          <UserRow key={u.user_id} u={u} actionLabel="Add" actionStyle="gwPrimaryBtn"
                            action={() => toggleUserInGroup(group.id, u.user_id)} />
                        ))}
                        {filteredNonMembers.length === 0 && (
                          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>
                            {groupSearchQuery ? "No users match." : "All users are already in this group."}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}

              <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
                {isNew ? (
                  <>
                    <button className="gwGhostBtn" type="button" onClick={() => setEditingGroup(null)}>Cancel</button>
                    <button className="gwPrimaryBtn" type="button" onClick={handleCreateGroup}
                      disabled={!newGroupName.trim()}>Create Group</button>
                  </>
                ) : (
                  <>
                    <button className="gwGhostBtn" type="button" onClick={() => { setEditingGroup(null); setGroupTab("members"); }}>Done</button>
                    <button className="gwDangerBtn" type="button" onClick={() => setDeleteGroupTarget(group.id)}
                      style={{ fontSize: 12 }}>Delete Group</button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete group confirmation */}
      {deleteGroupTarget && (() => {
        const group = groups.find((g) => g.id === deleteGroupTarget);
        if (!group) return null;
        return (
          <div className="gwOverlay" onClick={() => setDeleteGroupTarget(null)} style={{ zIndex: 110 }}>
            <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 400 }}>
              <div className="gwModalHeader">
                <div>
                  <div className="gwModalTitle">Delete Group</div>
                  <div className="gwModalSubtitle">This action cannot be undone.</div>
                </div>
                <button className="gwModalClose" type="button" onClick={() => setDeleteGroupTarget(null)}>
                  <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
                </button>
              </div>
              <div className="gwModalBody">
                <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                  Are you sure you want to delete <strong>{group.name}</strong>?
                  {group.userIds.length > 0 && ` This will remove ${group.userIds.length} member${group.userIds.length !== 1 ? "s" : ""} from the group.`}
                </p>
              </div>
              <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
                <button className="gwGhostBtn" type="button" onClick={() => setDeleteGroupTarget(null)}>Cancel</button>
                <button className="gwDangerBtn" type="button" onClick={confirmDeleteGroup}>
                  Delete Group
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export { UserManagementPage };
